import { useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { Doc } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { DocRelationships } from '@/new/components/doc-viewer/doc-relationships';
import { EditableField } from '@/new/components/editable-field';
import { Icon } from '@/new/components/icon';
import { MetadataDisplay } from '@/new/components/metadata-display';
import { ProcessingChip, getProcessingState } from '@/new/components/processing-chip';
import { Badge } from '@/new/components/shadcn/badge';
import { Label } from '@/new/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

interface DocDetailsPanelProps {
  doc: Doc & { keyFactsCount: number; relevantFactsCount: number };
}

export const DocDetailsPanel = ({ doc }: DocDetailsPanelProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const { hasPermission } = useHasPermission();
  const canUpdateDoc = hasPermission(Permission.DocumentUpdate);

  const { mutate: updateDocAuthor } = useUpdateDoc();
  const { mutate: updateDocDate } = useUpdateDoc();
  const { mutate: updateDocType } = useUpdateDoc();
  const { mutate: updateDocIsPleading } = useUpdateDoc();

  const handleUpdateDocAuthor = (value: string) => {
    updateDocAuthor({ matterId, docId: doc.id, field: 'author', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateAuthor, { documentId: doc.id });
  };

  const handleUpdateDocDate = (value: string) => {
    updateDocDate({ matterId, docId: doc.id, field: 'dateText', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateDate, { documentId: doc.id });
  };

  const handleUpdateDocType = (value: string) => {
    updateDocType({ matterId, docId: doc.id, field: 'type', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateType, { documentId: doc.id });
  };

  const handleUpdateDocIsPleading = (value: string) => {
    const isPleading = value === 'true' ? true : value === 'false' ? false : null;
    updateDocIsPleading({ matterId, docId: doc.id, field: 'isPleading', value: isPleading });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateIsPleading, { documentId: doc.id });
  };

  const formatAuthorString = (author: string | undefined | null) => {
    if (author === undefined || author === null) {
      return '';
    }

    if (author.length < 50) {
      return author;
    }

    const authors = author.split(', ');
    let trimmedAuthorString = '';

    for (const authorItem of authors) {
      trimmedAuthorString += authorItem;

      if (trimmedAuthorString.length >= 50) {
        return `${trimmedAuthorString} ...`;
      }

      trimmedAuthorString += ', ';
    }

    return `${author.slice(0, 50)} ...`;
  };

  return (
    <div className="space-y-3 px-6 py-5">
      <div className="flex flex-wrap items-center gap-2">
        <Chip className={cn('border', CHIP_COLOURS.extension)}>{doc.file.extension.toUpperCase()}</Chip>

        {doc.type && <Chip className={cn('border', CHIP_COLOURS.type)}>{doc.type}</Chip>}

        {doc.isPleading === true && <Chip className={cn('border', CHIP_COLOURS.isPleading)}>Pleading</Chip>}

        {doc.keyFactsCount > 0 && (
          <Chip className={cn('border', CHIP_COLOURS.key)}>
            <Icon className="lucide--key-round" />
            {doc.keyFactsCount} Key Fact{doc.keyFactsCount !== 1 ? 's' : ''}
          </Chip>
        )}

        {doc.relevantFactsCount > 0 && (
          <Chip className={cn('border', CHIP_COLOURS.hasRelevantFacts)}>
            <Icon className="lucide--check" />
            {doc.relevantFactsCount} Relevant Fact{doc.relevantFactsCount !== 1 ? 's' : ''}
          </Chip>
        )}
      </div>

      {/* New AI-generated title */}
      {doc.title && <p className="text-base font-medium">{doc.title}</p>}

      {/* Summary */}
      {doc.summary && <div className="mb-6 rounded-md text-sm leading-relaxed text-gray-700">{doc.summary}</div>}

      {/* Issues */}
      {doc.issues && doc.issues.length > 0 && (
        <div className="space-y-4">
          <Label className="text-xs uppercase">Issues</Label>
          <div className="my-2 flex flex-wrap gap-2">
            {doc.issues.map((issue, index) => (
              <Badge key={index} className="bg-blue-100/60 text-blue-600">
                {issue}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Document Information */}
      <div className="my-4 grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Document Date</Label>
          <div className="text-sm text-gray-900">
            {canUpdateDoc ? (
              <EditableField value={doc.dateText || 'N/A'} onSave={handleUpdateDocDate} />
            ) : (
              doc.dateText || 'N/A'
            )}
          </div>
        </div>

        {doc.author && (
          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Author</Label>
            <div className="text-sm text-gray-900">
              {canUpdateDoc ? (
                <EditableField value={formatAuthorString(doc.author)} onSave={handleUpdateDocAuthor} />
              ) : (
                formatAuthorString(doc.author)
              )}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Document Type</Label>
          <div className="text-sm text-gray-900">
            {canUpdateDoc ? (
              <EditableField value={doc.type || 'N/A'} onSave={handleUpdateDocType} />
            ) : (
              doc.type || 'N/A'
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Pleading</Label>
          <div className="w-fit text-sm text-gray-900">
            {canUpdateDoc ? (
              <Select
                value={doc.isPleading === true ? 'true' : doc.isPleading === false ? 'false' : 'null'}
                onValueChange={handleUpdateDocIsPleading}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="null">Unassigned</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : doc.isPleading === true ? (
              'Yes'
            ) : doc.isPleading === false ? (
              'No'
            ) : (
              'Unassigned'
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Facts Count</Label>
          <div className="text-sm text-gray-900">{doc.factsCount || 0}</div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Upload Date</Label>
          <div className="text-sm text-gray-900">{formatDate(doc.createdDate)}</div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Status</Label>
          <div className="text-sm text-gray-900">
            <ProcessingChip processingState={getProcessingState(doc)} />
          </div>
        </div>
      </div>

      {/* File Metadata */}
      {doc.file.fileMetadata && <MetadataDisplay metadata={doc.file.fileMetadata} />}

      {/* Document Relationships */}

      {((doc.file.parentFiles && doc.file.parentFiles.length > 0) ||
        (doc.file.childFiles && doc.file.childFiles.length > 0)) && <DocRelationships doc={doc} />}
    </div>
  );
};
